import { useEffect } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { Sidebar, SEO } from '@/shared/components'
import { useCurrentUser } from '@/features/auth/hooks/use-current-user'
import { ProfileTab, OrganizationTab, NotificationsTab, BillingTab, TeamTab } from '../components'
import { cn } from '@/shared/lib/utils'

type SettingsTab = 'profile' | 'organization' | 'team' | 'notifications' | 'billing'

const ALL_TABS: SettingsTab[] = ['profile', 'organization', 'team', 'notifications', 'billing']
const USER_TABS: SettingsTab[] = ['profile', 'notifications']

interface TabButtonProps {
  tab: SettingsTab
  isActive: boolean
  onClick: () => void
}

function TabButton({ tab, isActive, onClick }: TabButtonProps) {
  const { t } = useTranslation()

  return (
    <button
      onClick={onClick}
      className={cn(
        'px-5 py-3 text-sm font-medium transition-colors',
        isActive
          ? 'border-b-2 border-accent text-accent'
          : 'text-foreground-muted hover:text-foreground'
      )}
    >
      {t(`settings.tabs.${tab}`)}
    </button>
  )
}





export function SettingsPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { tab } = useParams<{ tab?: string }>()
  const { isAdmin } = useCurrentUser()

  // Determine available tabs based on role
  const availableTabs = isAdmin ? ALL_TABS : USER_TABS

  // Get current tab, default to 'profile'
  const currentTab = (tab as SettingsTab) || 'profile'

  // Redirect if accessing unauthorized tab or invalid tab
  useEffect(() => {
    if (!tab) {
      // No tab specified, redirect to default
      navigate('/settings/profile', { replace: true })
      return
    }

    const isValidTab = ALL_TABS.includes(tab as SettingsTab)
    const hasAccess = availableTabs.includes(tab as SettingsTab)

    if (!isValidTab || !hasAccess) {
      // Invalid tab or no access, redirect to profile
      navigate('/settings/profile', { replace: true })
    }
  }, [tab, availableTabs, navigate])

  const handleTabClick = (selectedTab: SettingsTab) => {
    navigate(`/settings/${selectedTab}`)
  }

  const renderTabContent = () => {
    switch (currentTab) {
      case 'profile':
        return <ProfileTab />
      case 'organization':
        return <OrganizationTab />
      case 'team':
        return <TeamTab />
      case 'notifications':
        return <NotificationsTab />
      case 'billing':
        return <BillingTab />
      default:
        return <ProfileTab />
    }
  }

  return (
    <div className="flex h-screen bg-background">
      <SEO titleKey="seo.settings.title" noIndex />
      <Sidebar />

      <main className="flex flex-1 flex-col overflow-auto bg-background-secondary">
        {/* Header */}
        <div className="px-8 pt-6">
          <h1 className="text-2xl font-semibold text-foreground">
            {t('settings.title')}
          </h1>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-border px-8">
          <div className="flex">
            {availableTabs.map((tabItem) => (
              <TabButton
                key={tabItem}
                tab={tabItem}
                isActive={currentTab === tabItem}
                onClick={() => handleTabClick(tabItem)}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8">
          <div className="max-w-2xl">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  )
}
